/*
  Flair cart + freshness helpers. Loaded on every page, because cart-drawer.liquid
  is rendered from header-group.

  1. addUpsellToCart(button, variantId)
     Adds an upsell line item. The button element is passed in explicitly. An
     earlier version read the deprecated global `window.event`, which is
     undefined in Firefox and Safari, so every upsell button silently failed
     in those browsers.

     CART EVENT SHAPE - READ THIS BEFORE CHANGING refreshCart()
     Horizon's <cart-items-component> listens for 'cart:update' and reads
     event.detail.data.sections[sectionId] (morphs that HTML in) or, when there
     are no sections, re-fetches the section itself. An earlier version fired a
     bare CustomEvent with no detail, so the handler threw on `event.detail.data`,
     the drawer never re-rendered, and the 1.2s fallback below reloaded the
     whole page with the drawer closed. We now ask /cart/add.js for the cart
     section and dispatch the event in the shape the theme expects.

  2. Cart back/forward cache guard
     When a shopper returns via the browser back button the page is restored
     from bfcache with the cart markup frozen at the moment they left. If the
     cart changed in the meantime (or they checked out), the drawer disagrees
     with the server. On restore we re-check the real cart.

  3. Product inventory freshness guard
     layout/theme.liquid deliberately drops no-cache headers and relies on JS
     for "inventory freshness" - but nothing was actually doing that job. A
     shopper could sit on a cached product page for a sold-out variant, add it,
     and only find out at checkout. We snapshot variant availability on load and
     re-check on bfcache restore.

  HARD RELOAD vs SOFT REFRESH - READ THIS BEFORE TOUCHING onRestore()
  Only a genuine bfcache restore (pageshow with event.persisted) may reload the
  page. A plain tab switch must not.

  Why: data-flair-cart-count lives on the drawer in header-group. Changing a
  quantity on /cart updates the server cart and the cart section, but does not
  re-render the header, so that attribute goes stale straight away. The previous
  version treated any mismatch on visibilitychange as drift and called
  location.reload(), which meant switching to another tab and back after any
  cart edit blew the page away mid-session - scroll position, open drawer, typed
  discount code and all. On tab focus we now ask the theme to re-render the cart
  (cart:update), which restamps the attribute, and leave the page alone.
  Inventory re-checks are likewise bfcache-only, since that path also reloads.

  CART COUNT - READ THIS BEFORE CHANGING readRenderedCount()
  Do NOT parse #cart-bubble-text. That element contains a visually-hidden
  accessibility label ("Cart count: 3") AND the visible count span, so
  stripping non-digits out of its textContent yields "33" for a 3-item cart.
  That false mismatch made this script reload on every restore and every tab
  focus. The count comes from the data-flair-cart-count attribute that
  cart-drawer.liquid stamps on <cart-drawer-component> instead.

  WHY A SNAPSHOT IN MEMORY WORKS
  bfcache preserves JavaScript heap state, so a variable captured at render
  time is still populated when the page is restored. That is what lets us
  compare "what this page was rendered against" with "what is true now"
  without stamping anything into the bespoke product sections.
*/
(function () {
  'use strict';

  function route(path) {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    return root.replace(/\/$/, '') + path;
  }

  function onIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      window.setTimeout(fn, 1200);
    }
  }

  // ---------------------------------------------------------------- cart ---

  function readRenderedCount() {
    var el = document.querySelector('[data-flair-cart-count]');
    if (!el) return null;
    var n = parseInt(el.getAttribute('data-flair-cart-count'), 10);
    return isNaN(n) ? null : n;
  }

  function cartSectionId() {
    var el = document.querySelector('cart-items-component');
    return el && el.dataset && el.dataset.sectionId ? el.dataset.sectionId : null;
  }

  /**
   * Ask the theme to re-render the cart. Mirrors the detail shape of Horizon's
   * CartUpdateEvent (assets/events.js) so <cart-items-component> can consume it:
   * with `sections` it morphs that HTML in, without it re-fetches the section.
   * @param {object} [resource] - the /cart/add.js response, if we have one
   * @param {object} [sections] - { [sectionId]: html } from that response
   */
  function refreshCart(resource, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: resource || {},
          sourceId: 'flair-cart-upsell',
          data: {
            source: 'flair-cart-upsell',
            itemCount: resource && resource.items ? resource.items.length : 0,
            sections: sections || undefined
          }
        }
      })
    );
  }

  window.addUpsellToCart = function (button, variantId) {
    if (!button || button.disabled) return;

    var originalText = button.textContent;
    var countBefore = readRenderedCount();
    var sectionId = cartSectionId();

    button.textContent = 'Adding…';
    button.disabled = true;

    var payload = { items: [{ id: variantId, quantity: 1 }] };
    if (sectionId) payload.sections = sectionId;

    fetch(route('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(
              data && data.description ? data.description : 'Could not add to cart'
            );
          }
          return data;
        });
      })
      .then(function (data) {
        refreshCart(data, data && data.sections);

        // If the theme did not re-render the drawer, fall back to a reload so the
        // shopper is never left looking at a cart that disagrees with the server.
        window.setTimeout(function () {
          var countAfter = readRenderedCount();
          if (countBefore !== null && countAfter !== null && countAfter !== countBefore) {
            return;
          }
          window.location.reload();
        }, 1200);
      })
      .catch(function (error) {
        console.error('Flair upsell add failed:', error);
        button.textContent = 'Unavailable';
        window.setTimeout(function () {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      });
  };

  var reconciling = false;

  /**
   * @param {boolean} hard - true only for a bfcache restore. When the server
   *   cart and the rendered cart disagree, `hard` reloads the page; otherwise
   *   we ask the theme to re-render the cart section and leave the page intact.
   * @returns {Promise<boolean>} resolves true when a reload has been triggered.
   */
  function reconcileCart(hard) {
    if (reconciling) return Promise.resolve(false);
    var rendered = readRenderedCount();
    if (rendered === null) return Promise.resolve(false);

    reconciling = true;
    return fetch(route('/cart.js'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var drifted =
          cart && typeof cart.item_count === 'number' && cart.item_count !== rendered;

        if (!drifted) {
          reconciling = false;
          return false;
        }

        if (hard) {
          window.location.reload();
          return true;
        }

        // Soft path: re-render rather than reload. Whatever the shopper was
        // doing on the page survives.
        refreshCart(cart);
        reconciling = false;
        return false;
      })
      .catch(function () {
        reconciling = false;
        return false;
      });
  }

  // ----------------------------------------------------------- inventory ---

  var productHandle = null;
  var availabilitySnapshot = null;
  var checkingInventory = false;

  function currentProductHandle() {
    if (!document.body || document.body.className.indexOf('template-product') === -1) {
      return null;
    }
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function fetchAvailability(handle) {
    return fetch(route('/products/' + handle + '.js'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Product fetch failed');
        return response.json();
      })
      .then(function (product) {
        var map = {};
        (product.variants || []).forEach(function (variant) {
          map[String(variant.id)] = variant.available === true;
        });
        return map;
      });
  }

  function snapshotDiffers(before, after) {
    if (!before || !after) return false;
    var ids = Object.keys(before);
    for (var i = 0; i < ids.length; i++) {
      if (Object.prototype.hasOwnProperty.call(after, ids[i]) && after[ids[i]] !== before[ids[i]]) {
        return true;
      }
    }
    // A variant appearing or disappearing entirely also matters.
    return Object.keys(after).length !== ids.length;
  }

  function captureAvailability() {
    productHandle = currentProductHandle();
    if (!productHandle) return;
    onIdle(function () {
      fetchAvailability(productHandle)
        .then(function (map) {
          availabilitySnapshot = map;
        })
        .catch(function () {});
    });
  }

  function reconcileInventory() {
    if (checkingInventory || !productHandle || !availabilitySnapshot) return;
    checkingInventory = true;
    fetchAvailability(productHandle)
      .then(function (map) {
        if (snapshotDiffers(availabilitySnapshot, map)) {
          window.location.reload();
          return;
        }
        checkingInventory = false;
      })
      .catch(function () {
        checkingInventory = false;
      });
  }

  // -------------------------------------------------------------- wiring ---

  function onRestore(hard) {
    // Cart first. If the cart drifted on a hard restore we reload anyway, which
    // refreshes inventory too, so there is no point firing both requests.
    reconcileCart(hard).then(function (reloading) {
      // Inventory can only respond by reloading, so it is bfcache-only too.
      if (!reloading && hard) reconcileInventory();
    });
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) onRestore(true);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') onRestore(false);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', captureAvailability);
  } else {
    captureAvailability();
  }
})();
